/**
 * Central i18n configuration for Vote.GameOn.
 *
 * Phase 0 (foundations): English only, scaffolding for future locales.
 * Future locales are listed below as `comingSoon` so the picker can show
 * progress chips and a "Help translate" link without shipping incomplete UIs.
 *
 * Wave order is documented in `docs/i18n.md`.
 */

export type LocaleStatus = "shipped" | "in_progress" | "coming_soon";

export interface LocaleDefinition {
  /** BCP-47 tag, e.g. "en", "pt-BR", "zh-Hans" */
  code: string;
  /** Display name in the locale itself, e.g. "Português (Brasil)" */
  nativeName: string;
  /** Display name in English, e.g. "Portuguese (Brazil)" */
  englishName: string;
  /** Two-letter region for flag display (ISO 3166-1 alpha-2) */
  flagRegion?: string;
  /** Wave bucket per the i18n roadmap */
  wave: 1 | 2 | 3 | 4 | 5;
  /** Translation status */
  status: LocaleStatus;
  /** Right-to-left script */
  rtl?: boolean;
  /** Approximate translation completeness (0–100). Undefined = unknown. */
  completeness?: number;
}

export const LOCALES: LocaleDefinition[] = [
  // Wave 0 — shipped
  { code: "en", nativeName: "English", englishName: "English", flagRegion: "GB", wave: 1, status: "shipped", completeness: 100 },

  // Wave 1 — LATAM + Iberia (Beta)
  { code: "es", nativeName: "Español", englishName: "Spanish", flagRegion: "ES", wave: 1, status: "shipped", completeness: 100 },
  { code: "pt-BR", nativeName: "Português (Brasil)", englishName: "Portuguese (Brazil)", flagRegion: "BR", wave: 1, status: "shipped", completeness: 100 },

  // Wave 2 — Continental Europe
  { code: "fr", nativeName: "Français", englishName: "French", flagRegion: "FR", wave: 2, status: "shipped", completeness: 100 },
  { code: "it", nativeName: "Italiano", englishName: "Italian", flagRegion: "IT", wave: 2, status: "shipped", completeness: 100 },
  { code: "de", nativeName: "Deutsch", englishName: "German", flagRegion: "DE", wave: 2, status: "coming_soon" },
  { code: "nl", nativeName: "Nederlands", englishName: "Dutch", flagRegion: "NL", wave: 2, status: "coming_soon" },

  // Wave 3 — Asian growth markets
  { code: "id", nativeName: "Bahasa Indonesia", englishName: "Indonesian", flagRegion: "ID", wave: 3, status: "coming_soon" },
  { code: "ja", nativeName: "日本語", englishName: "Japanese", flagRegion: "JP", wave: 3, status: "shipped", completeness: 100 },
  { code: "fil", nativeName: "Filipino", englishName: "Filipino", flagRegion: "PH", wave: 3, status: "coming_soon" },
  { code: "hi", nativeName: "हिन्दी", englishName: "Hindi", flagRegion: "IN", wave: 3, status: "coming_soon" },

  // Wave 4 — RTL + MENA
  { code: "ar", nativeName: "العربية", englishName: "Arabic", flagRegion: "SA", wave: 4, status: "shipped", completeness: 100, rtl: true },
  { code: "tr", nativeName: "Türkçe", englishName: "Turkish", flagRegion: "TR", wave: 4, status: "coming_soon" },
];

export const DEFAULT_LOCALE = "en";
export const FALLBACK_LOCALE = "en";

/** Pseudo-locale for QA: wraps every translated string in [!! …!!] so missing strings are visible. */
export const PSEUDO_LOCALE = "qps-ploc";

/** Namespaces — split per feature so we can lazy-load only what each route needs. */
export const NAMESPACES = [
  "common",
  "auth",
  "rsvp",
  "vote",
  "parent",
  "coach",
  "rpe",
  "training",
  "settings",
  "errors",
  "fillin",
] as const;

export type Namespace = (typeof NAMESPACES)[number];

export function getLocale(code: string): LocaleDefinition | undefined {
  return LOCALES.find((l) => l.code === code);
}

export function isRTL(code: string): boolean {
  return getLocale(code)?.rtl === true;
}

export function getShippedLocales(): LocaleDefinition[] {
  return LOCALES.filter((l) => l.status === "shipped" || l.status === "in_progress");
}
